import type { AccessCodeResultsResponse, ResultsResponse } from '../shared/api';
import { api, escapeHtml } from './http';
import { renderProtectedPage } from './layout';
import type { QuestionId, SurveyCatalogEntry, SurveyKey } from '../shared/survey';
import { installPinDialog, pinDialogMarkup } from './pin-gate';

export async function renderResultsPage(root: HTMLElement): Promise<void> {
  await renderProtectedPage(root, 'Survey Results', async () => {
    const content = root.querySelector<HTMLElement>('#protectedContent')!;
    content.innerHTML = '<div class="loading-state"><span class="spinner"></span>Loading results…</div>';
    const [{ surveys }, pinStatus] = await Promise.all([
      api<{ surveys: SurveyCatalogEntry[] }>('/api/surveys'),
      api<{ unlocked: boolean }>('/api/admin/pin'),
    ]);
    await loadResults(content, surveys, 'original', null, !pinStatus.unlocked, pinStatus.unlocked, createResultViewState());
  }, { wide: true });
}

export interface ResultSelection {
  questionId: QuestionId;
  option: string;
}

export interface ResultViewState {
  facets: Map<string, ResultSelection>;
  exclusions: Map<string, ResultSelection>;
}

async function loadResults(content: HTMLElement, surveys: SurveyCatalogEntry[], surveyKey: SurveyKey, neighborhood: string | null, showSmokeTest: boolean, liveDataUnlocked: boolean, viewState: ResultViewState): Promise<void> {
  try {
    const query = createResultsQuery(surveyKey, neighborhood, showSmokeTest, viewState);
    const results = await api<ResultsResponse>(`/api/admin/results${query}`);
    content.innerHTML = `
      <section class="dashboard-toolbar card">
        <label>Survey<select id="surveyFilter">${surveys.map((survey) => `<option value="${survey.key}"${survey.key === results.surveyKey ? ' selected' : ''}>${escapeHtml(survey.label)}</option>`).join('')}</select></label>
        <label>Results view<select id="neighborhoodFilter"><option value="">${results.showSmokeTest ? 'All smoke tests' : 'All neighborhoods'}</option>${results.neighborhoods.map((item) => `<option value="${escapeHtml(item)}"${item === results.neighborhood ? ' selected' : ''}>${escapeHtml(item)}</option>`).join('')}</select></label>
        <label class="checkbox-label smoke-test-filter"><input id="showSmokeTest" type="checkbox"${results.showSmokeTest ? ' checked' : ''}> Show smoke test</label>
        <button id="unlockLiveResults" class="secondary-button" type="button"${liveDataUnlocked ? ' hidden' : ''}>Enter PIN</button>
        <a class="secondary-button results-download" href="/api/admin/results.csv${escapeHtml(query)}">Download CSV</a>
        <div class="summary-stat"><strong>${results.responseCount}</strong><span>Responses</span></div>
        <div class="summary-stat"><strong>${results.householdCount}</strong><span>Households</span></div>
      </section>
      ${renderActiveModifiers(viewState)}
      <section class="card duplicate-panel">
        <div class="result-header">
          <div><p class="eyebrow">Access-code usage</p><h2>Most repeated access codes</h2></div>
          <div class="duplicate-summary" aria-label="Access-code duplication summary">
            <span><strong>${results.repeatedAccessCodeCount}</strong> codes used more than once</span>
            <span><strong>${results.additionalResponseCount}</strong> additional responses</span>
          </div>
        </div>
        ${results.duplicateAccessCodes.length === 0
          ? '<p class="empty-state">No access code has more than one response in this view.</p>'
          : `<div class="duplicate-table-wrap"><table class="duplicate-table">
              <thead><tr><th scope="col">Rank</th><th scope="col">Access code</th><th scope="col">Neighborhood</th><th scope="col">Responses</th></tr></thead>
              <tbody>${results.duplicateAccessCodes.map((entry, index) => `<tr class="duplicate-code-row" data-access-code="${entry.accessCode}">
                <td>${index + 1}</td><td><button class="duplicate-code-button" type="button" aria-expanded="false" aria-controls="duplicate-details-${entry.accessCode}"><code>${escapeHtml(entry.accessCode)}</code><span aria-hidden="true">⌄</span></button></td><td>${escapeHtml(entry.neighborhood)}</td><td><strong>${entry.responseCount}</strong></td>
              </tr><tr id="duplicate-details-${entry.accessCode}" class="duplicate-detail-row" hidden><td colspan="4"><div class="duplicate-detail-content"></div></td></tr>`).join('')}</tbody>
            </table></div>
            ${results.repeatedAccessCodeCount > results.duplicateAccessCodes.length ? `<p class="duplicate-limit">Showing the top ${results.duplicateAccessCodes.length} of ${results.repeatedAccessCodeCount} repeated codes.</p>` : ''}`}
      </section>
      <section class="card fraud-panel">
        <div class="result-header">
          <div><p class="eyebrow">Submission integrity</p><h2>Fraud detection signals</h2></div>
          <span>Sorted by submission count</span>
        </div>
        <p class="section-intro">Compare repeated network addresses and browsers with the number of access codes they used. These signals can be shared by legitimate household members and should be reviewed together.</p>
        <div class="fraud-grid">
          ${renderSignalTable('IP address', results.submissionsByIpAddress)}
          ${renderSignalTable('User agent', results.submissionsByUserAgent)}
        </div>
      </section>
      <div class="results-grid">
        ${results.questions.map((question, index) => renderResultQuestionCard(question, index, viewState)).join('')}
      </div>
      <section class="card feedback-panel">
        <div class="result-header"><div><p class="eyebrow">Optional responses</p><h2>Additional feedback</h2></div><span>${results.feedback.length} entries</span></div>
        ${results.feedback.length === 0 ? '<p class="empty-state">No additional feedback in this view.</p>' : `<div class="feedback-list">${results.feedback.map((entry) => `<article><p>${escapeHtml(entry.text)}</p><footer>${escapeHtml(entry.neighborhood)} · ${new Date(entry.submittedAt).toLocaleString()}</footer></article>`).join('')}</div>`}
      </section>
      ${pinDialogMarkup()}`;
    const openPinDialog = installPinDialog(content, () => loadResults(content, surveys, results.surveyKey, null, false, true, viewState));
    content.querySelector('#unlockLiveResults')?.addEventListener('click', openPinDialog);
    content.querySelector<HTMLSelectElement>('#surveyFilter')?.addEventListener('change', (event) => {
      void loadResults(content, surveys, (event.currentTarget as HTMLSelectElement).value as SurveyKey, null, results.showSmokeTest, liveDataUnlocked, createResultViewState());
    });
    content.querySelector<HTMLSelectElement>('#neighborhoodFilter')?.addEventListener('change', (event) => {
      const value = (event.currentTarget as HTMLSelectElement).value;
      void loadResults(content, surveys, results.surveyKey, value || null, results.showSmokeTest, liveDataUnlocked, viewState);
    });
    content.querySelector<HTMLInputElement>('#showSmokeTest')?.addEventListener('change', (event) => {
      const input = event.currentTarget as HTMLInputElement;
      if (!input.checked && !liveDataUnlocked) {
        input.checked = true;
        openPinDialog();
        return;
      }
      void loadResults(content, surveys, results.surveyKey, null, input.checked, liveDataUnlocked, viewState);
    });
    content.querySelectorAll<HTMLTableRowElement>('.duplicate-code-row').forEach((row) => {
      row.addEventListener('click', () => void toggleAccessCodeDetails(row, results.surveyKey, results.neighborhood, results.showSmokeTest, viewState));
    });
    installResultOptionMenus(content, viewState, () => loadResults(content, surveys, results.surveyKey, results.neighborhood, results.showSmokeTest, liveDataUnlocked, viewState));
  } catch (error) {
    content.innerHTML = `<div class="error-state">${escapeHtml(error instanceof Error ? error.message : 'Results could not be loaded.')}</div>`;
  }
}

export function renderResultQuestionCard(
  question: ResultsResponse['questions'][number],
  index: number,
  viewState?: ResultViewState,
  options: { label?: string; compact?: boolean } = {},
): string {
  return `<section class="card result-question${options.compact ? ' result-question-map' : ''}">
    ${options.label ? `<div class="map-result-label"><span>${escapeHtml(options.label)}</span></div>` : ''}
    <div class="question-heading"><span class="question-number" aria-label="Question ${index + 1}">${index + 1}</span><h2>${escapeHtml(question.questionText)}</h2></div>
    <div class="result-legend"><span><i class="raw-dot"></i>Raw respondents</span><span><i class="weighted-dot"></i>Household weighted</span></div>
    ${question.options.map((option) => {
      const key = selectionKey(question.questionId, option.option);
      const modifierClass = viewState?.exclusions.has(key) ? ' is-excluded' : viewState?.facets.has(key) ? ' is-faceted' : '';
      const modifierLabel = viewState?.exclusions.has(key) ? '<span class="result-option-status">Excluded</span>' : viewState?.facets.has(key) ? '<span class="result-option-status">Faceted</span>' : '';
      const interaction = viewState
        ? ` tabindex="0" data-question-id="${question.questionId}" data-option="${escapeHtml(option.option)}" aria-label="${escapeHtml(option.option)}. Right-click for result options."`
        : '';
      return `<div class="result-option${modifierClass}"${interaction}>
        <div class="result-option-label"><strong>${escapeHtml(option.option)}</strong></div>
        ${modifierLabel}
        <div class="result-metric"><span>${formatTotal(option.weightedTotal)}</span><span>weighted</span><span>${formatPercent(option.weightedPercent)}</span></div>
        <div class="bars"><div class="bar raw-bar" style="width:${option.rawPercent * 100}%"></div><div class="bar weighted-bar" style="width:${option.weightedPercent * 100}%"></div></div>
        <div class="result-metric"><span>${option.rawCount}</span><span>raw</span><span>${formatPercent(option.rawPercent)}</span></div>
      </div>`;
    }).join('')}
  </section>`;
}

function renderSignalTable(
  label: string,
  entries: ResultsResponse['submissionsByIpAddress'],
): string {
  return `<div class="signal-table-wrap"><h3>By ${label.toLowerCase()}</h3>
    ${entries.length === 0 ? '<p class="empty-state">No submissions in this view.</p>' : `<table class="signal-table">
      <thead><tr><th scope="col">${label}</th><th scope="col">Submissions</th><th scope="col">Codes</th></tr></thead>
      <tbody>${entries.map((entry) => `<tr><td title="${escapeHtml(entry.value)}"><code>${escapeHtml(entry.value)}</code></td><td><strong>${entry.responseCount}</strong></td><td>${entry.accessCodeCount}</td></tr>`).join('')}</tbody>
    </table>`}
  </div>`;
}

async function toggleAccessCodeDetails(
  row: HTMLTableRowElement,
  surveyKey: SurveyKey,
  neighborhood: string | null,
  showSmokeTest: boolean,
  viewState: ResultViewState,
): Promise<void> {
  const button = row.querySelector<HTMLButtonElement>('.duplicate-code-button')!;
  const details = row.nextElementSibling as HTMLTableRowElement;
  const target = details.querySelector<HTMLElement>('.duplicate-detail-content')!;
  const opening = details.hidden;
  details.hidden = !opening;
  button.setAttribute('aria-expanded', String(opening));
  row.classList.toggle('is-expanded', opening);
  if (!opening || target.dataset.loaded === 'true') return;
  target.innerHTML = '<div class="duplicate-detail-loading"><span class="spinner"></span>Loading question results…</div>';
  try {
    const query = createResultsQuery(surveyKey, neighborhood, showSmokeTest, viewState);
    const code = row.dataset.accessCode!;
    const results = await api<AccessCodeResultsResponse>(`/api/admin/results/access-codes/${encodeURIComponent(code)}${query}`);
    target.dataset.loaded = 'true';
    target.innerHTML = `<div class="duplicate-score-heading"><span>Question</span><span>Household (${results.responseCount})</span><span>Overall (${results.overallResponseCount})</span></div>
      <div class="duplicate-question-scores">${results.questions.map((question, index) => `<div class="duplicate-question-score">
        <span class="duplicate-score-number" title="${escapeHtml(question.questionText)}" aria-label="Question ${index + 1}: ${escapeHtml(question.questionText)}">${index + 1}</span>
        <span><span class="mobile-score-label">Household (${results.responseCount})</span>${formatScore(question.options, 'household')}</span>
        <span><span class="mobile-score-label">Overall (${results.overallResponseCount})</span>${formatScore(question.options, 'overall')}</span>
      </div>`).join('')}</div>`;
  } catch (error) {
    target.innerHTML = `<div class="error-state compact-error">${escapeHtml(error instanceof Error ? error.message : 'Question results could not be loaded.')}</div>`;
  }
}

function createResultsQuery(surveyKey: SurveyKey, neighborhood: string | null, showSmokeTest: boolean, viewState: ResultViewState): string {
  const parameters = new URLSearchParams();
  if (surveyKey !== 'original') parameters.set('surveyKey', surveyKey);
  if (neighborhood) parameters.set('neighborhood', neighborhood);
  if (showSmokeTest) parameters.set('showSmokeTest', 'true');
  viewState.facets.forEach(({ questionId, option }) => parameters.append('facet', `${questionId}:${option}`));
  viewState.exclusions.forEach(({ questionId, option }) => parameters.append('exclude', `${questionId}:${option}`));
  const query = parameters.toString();
  return query ? `?${query}` : '';
}

export function createResultViewState(): ResultViewState {
  return { facets: new Map(), exclusions: new Map() };
}

function selectionKey(questionId: QuestionId, option: string): string {
  return `${questionId}\u0000${option}`;
}

export function renderActiveModifiers(viewState: ResultViewState): string {
  if (viewState.facets.size === 0 && viewState.exclusions.size === 0) return '';
  const chips = [
    ...[...viewState.facets.values()].map((selection) => `<span><strong>Facet Q${Number(selection.questionId.slice(1))}</strong> ${escapeHtml(selection.option)}</span>`),
    ...[...viewState.exclusions.values()].map((selection) => `<span><strong>Excluded Q${Number(selection.questionId.slice(1))}</strong> ${escapeHtml(selection.option)}</span>`),
  ];
  return `<section class="active-result-modifiers" aria-label="Active result filters">${chips.join('')}</section>`;
}

export function installResultOptionMenus(
  content: HTMLElement,
  viewState: ResultViewState,
  reload: () => Promise<void>,
): void {
  content.querySelectorAll<HTMLElement>('.result-option').forEach((optionElement) => {
    const open = (clientX: number, clientY: number) => {
      const questionId = optionElement.dataset.questionId as QuestionId;
      const option = optionElement.dataset.option!;
      const key = selectionKey(questionId, option);
      const active = viewState.exclusions.has(key) || viewState.facets.has(key);
      content.querySelector('.result-option-popover')?.remove();
      const popover = document.createElement('div');
      popover.className = 'result-option-popover';
      popover.setAttribute('role', 'menu');
      popover.setAttribute('aria-label', `Options for ${option}`);
      popover.innerHTML = active
        ? '<button type="button" role="menuitem" data-result-action="cancel">Cancel</button>'
        : '<button type="button" role="menuitem" data-result-action="facet">Facet</button><button type="button" role="menuitem" data-result-action="exclude">Exclude</button>';
      content.append(popover);
      const bounds = popover.getBoundingClientRect();
      popover.style.left = `${Math.max(8, Math.min(clientX, window.innerWidth - bounds.width - 8))}px`;
      popover.style.top = `${Math.max(8, Math.min(clientY, window.innerHeight - bounds.height - 8))}px`;

      const close = () => {
        popover.remove();
        document.removeEventListener('keydown', onDocumentKeydown);
      };
      const onDocumentKeydown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') close();
      };
      document.addEventListener('keydown', onDocumentKeydown);
      setTimeout(() => document.addEventListener('pointerdown', close, { once: true }), 0);
      popover.addEventListener('pointerdown', (event) => event.stopPropagation());
      popover.addEventListener('click', (event) => {
        const button = (event.target as Element).closest<HTMLButtonElement>('[data-result-action]');
        if (!button) return;
        const action = button.dataset.resultAction;
        if (action === 'cancel') {
          viewState.exclusions.delete(key);
          viewState.facets.delete(key);
        } else if (action === 'exclude') {
          viewState.exclusions.set(key, { questionId, option });
          viewState.facets.delete(key);
        } else if (action === 'facet') {
          viewState.facets.set(key, { questionId, option });
          viewState.exclusions.delete(key);
        }
        close();
        void reload();
      });
      popover.querySelector<HTMLButtonElement>('button')?.focus();
    };
    optionElement.addEventListener('contextmenu', (event) => {
      event.preventDefault();
      open(event.clientX, event.clientY);
    });
    optionElement.addEventListener('click', (event) => {
      if (!window.matchMedia('(hover: none)').matches) return;
      event.preventDefault();
      const bounds = optionElement.getBoundingClientRect();
      open(bounds.left + bounds.width / 2, bounds.top + bounds.height / 2);
    });
    optionElement.addEventListener('keydown', (event) => {
      if (event.key !== 'ContextMenu' && !(event.shiftKey && event.key === 'F10')) return;
      event.preventDefault();
      const bounds = optionElement.getBoundingClientRect();
      open(bounds.left + 16, bounds.top + 16);
    });
  });
}

function formatScore(
  options: AccessCodeResultsResponse['questions'][number]['options'],
  scope: 'household' | 'overall',
): string {
  return options
    .filter((option) => option[`${scope}Count`] > 0)
    .map((option) => `<strong>${escapeHtml(option.option)}</strong> ${formatPercent(option[`${scope}Percent`])} <small>(${option[`${scope}Count`]})</small>`)
    .join('<span class="score-separator"> · </span>');
}

function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

function formatTotal(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}
